export type LifeBaseErrorCategory = "validation" | "processing" | "unavailable" | "unexpected";

export const LIFE_BASE_ERROR_MESSAGES: Record<LifeBaseErrorCategory, string> = {
  validation: "Algumas informações precisam ser corrigidas antes de continuar.",
  processing: "A importação não pôde ser concluída. Verifique os dados enviados ou entre em contato com o suporte.",
  unavailable: "O serviço está temporariamente indisponível. Tente novamente mais tarde.",
  unexpected: "Não foi possível concluir a operação. Tente novamente ou entre em contato com o suporte.",
};

const TECHNICAL_MESSAGE_PATTERNS = [
  /\b(?:sql|mysql|tidb|insert|update|delete|select|constraint|duplicate entry|syntax error|drizzle)\b/i,
  /\b(?:http|https)\b/i,
  /\b(?:status\s*[:=]?\s*(?:4\d\d|5\d\d)|(?:4\d\d|5\d\d)\b)/i,
  /\b(?:stack|trace|exception|internal server error|request failed|fetch failed|network error|axios|trpcclienterror)\b/i,
  /\b(?:timeout|timed out|econn|enotfound|socket hang up|gateway|bad gateway)\b/i,
  /\b(?:responsebody|response body|payload|json|html|doctype)\b/i,
  /\b(?:i4h|ulisses|api key|token|secret|s3 key|upload id)\b/i,
  /\bat\s+[\w$.<>]+\s*\(/i,
  /\{.*\}|\[object Object\]/i,
];

const UNAVAILABLE_PATTERNS = [
  /\b(?:temporariamente|indispon[ií]vel|manuten[cç][aã]o|timeout|timed out|gateway|service unavailable|bad gateway|too many requests|rate limit)\b/i,
  /\b(?:503|504|502|429)\b/,
];

const VALIDATION_PATTERNS = [
  /\b(?:inv[aá]lid|inconsisten|corrig|obrigat|required|campo|planilha|modelo|layout|aba|empresa|escopo|cnpj|cpf|titular|dependente|plano|dados enviados)\b/i,
  /\b(?:template|arquivo|formato|xlsx|xlsm|csv|linha|coluna|preench)\b/i,
];

const PROCESSING_PATTERNS = [
  /\b(?:processamento|processar|processado|importa[cç][aã]o|lote|rascunho|envio|confirm|hist[oó]rico|resumo)\b/i,
  /\b(?:persist|salvar|gravar|concluir a opera[cç][aã]o|concluir o envio)\b/i,
];

const VISIBLE_REPLACEMENTS: Array<[RegExp, string]> = [
  [/Base de Vidas/gi, "Base de Pessoas"],
  [/base de vidas/gi, "base de pessoas"],
  [/pr[eé]-?valida[cç][aã]o/gi, "conferência da planilha"],
  [/validar lote/gi, "conferir planilha"],
  [/preparar lote para envio/gi, "preparar envio"],
  [/preparar lote/gi, "preparar envio"],
  [/enviar lote/gi, "concluir envio"],
  [/lotes/gi, "envios"],
  [/lote/gi, "envio"],
  [/Status operacional/gi, "Situação do envio"],
  [/status operacional/gi, "situação do envio"],
  [/Template/gi, "Modelo"],
  [/template/gi, "modelo"],
  [/Processamento controlado/gi, "Processando dados"],
  [/I4H/gi, "plataforma externa"],
  [/i4h\/SE/gi, "plataforma externa"],
  [/i4h/gi, "plataforma externa"],
  [/ulisses/gi, "plataforma externa"],
];

function normalizeWhitespace(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

function extractMessage(error: unknown) {
  if (!error) return "";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message || "";

  if (typeof error === "object") {
    const candidate = error as {
      message?: unknown;
      shape?: { message?: unknown; data?: { code?: unknown; httpStatus?: unknown } };
      data?: { code?: unknown; httpStatus?: unknown; zodError?: unknown };
      code?: unknown;
      httpStatus?: unknown;
    };

    if (typeof candidate.message === "string") return candidate.message;
    if (typeof candidate.shape?.message === "string") return candidate.shape.message;
  }

  return "";
}

function extractCode(error: unknown) {
  if (!error || typeof error !== "object") return "";
  const candidate = error as {
    code?: unknown;
    data?: { code?: unknown };
    shape?: { data?: { code?: unknown } };
  };

  return String(candidate.data?.code || candidate.shape?.data?.code || candidate.code || "");
}

function extractHttpStatus(error: unknown) {
  if (!error || typeof error !== "object") return null;
  const candidate = error as {
    httpStatus?: unknown;
    data?: { httpStatus?: unknown };
    shape?: { data?: { httpStatus?: unknown } };
  };

  const value = candidate.data?.httpStatus || candidate.shape?.data?.httpStatus || candidate.httpStatus;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function hasPatternMatch(message: string, patterns: RegExp[]) {
  return patterns.some((pattern) => pattern.test(message));
}

export function sanitizeLifeBaseVisibleCopy(value?: string | null) {
  if (!value) return "";

  let sanitized = String(value);

  for (const [pattern, replacement] of VISIBLE_REPLACEMENTS) {
    sanitized = sanitized.replace(pattern, replacement);
  }

  sanitized = sanitized
    .replace(/\b(?:erro|error)\s*(?:http|sql)?\s*[:=-]?\s*(?:4\d\d|5\d\d)\b/gi, "")
    .replace(/\b(?:http|https)\s*\d{3}\b/gi, "")
    .replace(/\b(?:sqlstate|errno|code)\s*[:=-]?\s*[A-Z0-9_-]+\b/gi, "")
    .replace(/\bat\s+[\w$.<>]+\s*\([^)]*\)/g, "")
    .replace(/[{}<>\[\]]/g, " ");

  return normalizeWhitespace(sanitized);
}

export function classifyLifeBaseError(error: unknown, preferredCategory?: LifeBaseErrorCategory): LifeBaseErrorCategory {
  if (preferredCategory) return preferredCategory;

  const message = `${extractMessage(error)} ${extractCode(error)}`.trim();
  const normalizedMessage = sanitizeLifeBaseVisibleCopy(message).toLowerCase();
  const code = extractCode(error).toUpperCase();
  const httpStatus = extractHttpStatus(error);

  if (
    code === "BAD_REQUEST"
    || code === "UNPROCESSABLE_CONTENT"
    || code === "PARSE_ERROR"
    || code === "FORBIDDEN"
    || hasPatternMatch(normalizedMessage, VALIDATION_PATTERNS)
  ) {
    return "validation";
  }

  if (
    httpStatus === 429
    || (httpStatus !== null && httpStatus >= 500)
    || code === "SERVICE_UNAVAILABLE"
    || code === "TIMEOUT"
    || code === "TOO_MANY_REQUESTS"
    || hasPatternMatch(normalizedMessage, UNAVAILABLE_PATTERNS)
  ) {
    return "unavailable";
  }

  if (
    code === "PRECONDITION_FAILED"
    || code === "CONFLICT"
    || code === "NOT_FOUND"
    || code === "INTERNAL_SERVER_ERROR"
    || hasPatternMatch(normalizedMessage, PROCESSING_PATTERNS)
  ) {
    return "processing";
  }

  return "unexpected";
}

export function isTechnicalLifeBaseErrorMessage(message?: string | null) {
  if (!message) return false;
  return hasPatternMatch(message, TECHNICAL_MESSAGE_PATTERNS);
}

export function getLifeBaseFriendlyErrorMessage(args: {
  error?: unknown;
  fallback?: string;
  preferredCategory?: LifeBaseErrorCategory;
  allowValidationDetails?: boolean;
}) {
  const category = classifyLifeBaseError(args.error, args.preferredCategory);
  const defaultMessage = args.fallback || LIFE_BASE_ERROR_MESSAGES[category];
  const rawMessage = extractMessage(args.error);
  const sanitizedMessage = sanitizeLifeBaseVisibleCopy(rawMessage);

  if (!sanitizedMessage) {
    return defaultMessage;
  }

  if (isTechnicalLifeBaseErrorMessage(rawMessage)) {
    return defaultMessage;
  }

  if (category === "validation" && args.allowValidationDetails) {
    return sanitizedMessage;
  }

  if (category === "processing" && sanitizedMessage.length <= 180 && !isTechnicalLifeBaseErrorMessage(sanitizedMessage)) {
    return sanitizedMessage;
  }

  return defaultMessage;
}

export function getLifeBaseFriendlyOperationalMessage(message?: string | null, preferredCategory?: LifeBaseErrorCategory) {
  const sanitizedMessage = sanitizeLifeBaseVisibleCopy(message);
  if (!sanitizedMessage || isTechnicalLifeBaseErrorMessage(message)) {
    return LIFE_BASE_ERROR_MESSAGES[preferredCategory || "unexpected"];
  }

  return sanitizedMessage;
}
